import { Tooltip } from '@mui/material';
import AutocompleteInput from 'components/Autocomplete/AutocompleteInput';
import React from 'react';
import propTypes from 'prop-types';
import { Info } from 'lucide-react';
import Button from 'tailwind-components/Button/Button';
import Input from 'tailwind-components/Input/Input';
import { NON_ACTIONABLE_RA_STATUSES } from 'constants/enum';
import { sanitizeDirtyHTML } from 'helpers/strings';
import CarrierAppointmentVideo from 'views/MyAppointments/CarrierAppointmentVideo/CarrierAppointmentVideo';
import ChevronIcon from 'icons/Chevron';
import { cn } from 'lib/utils';

export default function AppointmentDetailsRequestBody({
  appointmentDetails,
  setAppointmentDetails,
  isLoading,
  credentials,
  errors,
  action,
  actionOptions,
  setAction,
  handleCredentialChange,
  handleSubmit,
  hasInstructionsSection,
  hasNote,
  showNewAppointmentVideo,
  newAppointmentsVideoURL,
  isInstructionsExpanded,
  setIsInstructionsExpanded,
  instructionsToggleLabel,
  onOpenAcknowledgement
}) {
  const detailsActionTooltipSlotProps = {
    tooltip: {
      sx: {
        fontSize: '0.875rem',
        fontWeight: 500,
        padding: '8px 12px',
        maxWidth: 320,
        textAlign: 'center',
        color: '#fff'
      }
    }
  };

  return (
    <>
      <div className='mb-5 flex flex-col gap-4'>
        <p className='m-0 text-lg'>
          <strong>Carrier:</strong> {appointmentDetails.carrier}
        </p>
        <p className='m-0 text-lg'>
          <strong>Request Type:</strong> {appointmentDetails.request_type}
        </p>

        {(appointmentDetails.appointments_information || []).length > 0 && (
          <p className='m-0 text-lg'>
            <strong>Requested States: </strong>
            <span>
              {appointmentDetails.carrier === 'BCBS'
                ? appointmentDetails.uniqueBcbsState
                : appointmentDetails.appointments_information
                    .map(row => row.Appointment_State_1)
                    .join(', ')}
            </span>
          </p>
        )}

        {appointmentDetails.substatus && (
          <div className='flex flex-col gap-1.5'>
            <p className='m-0 text-lg'>
              <strong>
                {NON_ACTIONABLE_RA_STATUSES.has(appointmentDetails.substatus)
                  ? 'Note'
                  : 'Action Required'}
              </strong>
            </p>

            <span
              className={cn(
                'inline-block w-fit rounded-[15px] px-2 py-0.5 text-sm font-medium text-white',
                NON_ACTIONABLE_RA_STATUSES.has(appointmentDetails.substatus)
                  ? 'bg-[#93d500]'
                  : 'bg-red-600'
              )}
            >
              {appointmentDetails.substatus}
            </span>

            {appointmentDetails.substatus === 'Agent Login Needed' && (
              <form
                className='mt-3 flex flex-col gap-2.5'
                onSubmit={e =>
                  handleSubmit(e, () => setAppointmentDetails(null))
                }
              >
                <div className='flex flex-col gap-2.5 sm:flex-row sm:gap-2.5'>
                  <Input
                    label='Username'
                    type='text'
                    value={credentials.username}
                    error={errors.username}
                    disabled={isLoading}
                    onChange={e =>
                      handleCredentialChange('username', e.target.value)
                    }
                  />

                  <Input
                    label='Password'
                    type='text'
                    value={credentials.password}
                    error={errors.password}
                    disabled={isLoading}
                    onChange={e =>
                      handleCredentialChange('password', e.target.value)
                    }
                  />
                </div>

                <Button
                  type='submit'
                  disabled={isLoading}
                  isLoading={isLoading}
                  variant='primaryLight'
                  text='Submit credentials'
                  fill
                  size='medium'
                />
              </form>
            )}
          </div>
        )}

        {hasInstructionsSection && (
          <>
            <div className='my-2 h-px bg-gray-200' />
            <button
              type='button'
              className={cn(
                'inline-flex cursor-pointer items-center gap-1.5 border-none bg-transparent p-0 py-1 text-left font-semibold text-primary-emphasis-light transition-colors hover:text-primary-darkened',
                isInstructionsExpanded ? 'mb-2.5' : 'mb-0'
              )}
              onClick={() => setIsInstructionsExpanded(prev => !prev)}
            >
              <Info className='size-5 shrink-0' />
              <span className='pt-0.5 text-xs font-semibold'>
                {instructionsToggleLabel}
              </span>
              <ChevronIcon
                className={cn(
                  'inline-block h-4 w-4 shrink-0 transition-transform',
                  isInstructionsExpanded && 'rotate-180'
                )}
              />
            </button>
            {isInstructionsExpanded && (
              <div className='animate-[slideDown_200ms_ease] border-l-[3px] border-grey-light/50 px-3.5 text-xs text-gray-600 [&_a]:font-medium [&_a]:text-primary-emphasis-light [&_a]:no-underline hover:[&_a]:underline [&_ol]:my-1.5 [&_ol]:pl-[18px] [&_p]:my-1.5 [&_p]:first:mt-0 [&_ul]:my-1.5 [&_ul]:pl-[18px]'>
                {hasNote && (
                  <div
                    className='m-0 leading-relaxed [&_a]:font-medium [&_a]:text-primary-emphasis-light [&_a]:no-underline hover:[&_a]:underline'
                    dangerouslySetInnerHTML={sanitizeDirtyHTML(
                      appointmentDetails.custom_note_for_agent
                    )}
                  />
                )}
                {showNewAppointmentVideo && (
                  <div className={hasNote ? 'mt-3.5' : undefined}>
                    <CarrierAppointmentVideo src={newAppointmentsVideoURL} />
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <div className='flex flex-col gap-1.5'>
          <span
            id='available-appointment-actions-label'
            className='text-base font-semibold text-black'
          >
            Available Appointment Actions
          </span>
          <AutocompleteInput
            disableSearch
            hideFloatingLabel
            multiple={false}
            id='available-appointment-actions'
            aria-labelledby='available-appointment-actions-label'
            placeholder='Choose an action'
            value={action?.label || ''}
            options={actionOptions}
            getOptionLabel={option => option.label}
            getOptionSecondaryLabel={option =>
              typeof option.tooltip === 'string' && option.tooltip.trim()
                ? option.tooltip
                : null
            }
            onValueChanges={e => {
              const selectedAction = actionOptions.find(
                option => option.label === e.target.value
              );
              setAction(selectedAction);
            }}
          />
        </div>
      </div>
      <div className='flex gap-2.5'>
        <Button
          className='flex-1'
          disabled={isLoading}
          onClick={() => setAppointmentDetails(null)}
          fill
          text='Cancel'
          variant='danger'
          size='medium'
        />
        {action &&
          (action.tooltip ? (
            <Tooltip
              title={action.tooltip}
              placement='top'
              arrow
              slotProps={detailsActionTooltipSlotProps}
            >
              <span className='inline-flex min-w-0 flex-1 [&>button]:w-full'>
                <Button
                  onClick={onOpenAcknowledgement}
                  disabled={isLoading}
                  isLoading={isLoading}
                  variant='primaryLight'
                  text={action.button}
                  fill
                  size='medium'
                />
              </span>
            </Tooltip>
          ) : (
            <Button
              className='flex-1'
              onClick={onOpenAcknowledgement}
              disabled={isLoading}
              isLoading={isLoading}
              variant='primaryLight'
              text={action.button}
              fill
              size='medium'
            />
          ))}
      </div>
    </>
  );
}

AppointmentDetailsRequestBody.propTypes = {
  appointmentDetails: propTypes.object.isRequired,
  setAppointmentDetails: propTypes.func.isRequired,
  isLoading: propTypes.bool.isRequired,
  credentials: propTypes.object.isRequired,
  errors: propTypes.object.isRequired,
  action: propTypes.object,
  actionOptions: propTypes.arrayOf(propTypes.object).isRequired,
  setAction: propTypes.func.isRequired,
  handleCredentialChange: propTypes.func.isRequired,
  handleSubmit: propTypes.func.isRequired,
  hasInstructionsSection: propTypes.bool.isRequired,
  hasNote: propTypes.bool.isRequired,
  showNewAppointmentVideo: propTypes.bool.isRequired,
  newAppointmentsVideoURL: propTypes.string,
  isInstructionsExpanded: propTypes.bool.isRequired,
  setIsInstructionsExpanded: propTypes.func.isRequired,
  instructionsToggleLabel: propTypes.string.isRequired,
  onOpenAcknowledgement: propTypes.func.isRequired
};
